import { type AppearancePreferencesChangedProperties } from "analytics";
import { registerPosthogProperties } from "config/posthog-runtime";
import { resolveAppearanceMode } from "./apply-appearance";
import type { SolarCycleResolution } from "./solar-cycle";
import type { AppearancePreferences, ResolvedAppearanceMode } from "./types";

export type AppearancePreferenceKey = AppearancePreferencesChangedProperties["preference"];

type SolarAnalyticsContext = Pick<SolarCycleResolution, "phase" | "theme" | "mode"> | null;

export function buildAppearancePosthogProperties(
  preferences: AppearancePreferences,
  resolvedMode: ResolvedAppearanceMode,
  options?: {
    solarPhase: SolarCycleResolution["phase"] | null;
    resolvedTheme?: AppearancePreferences["theme"];
  }
): Record<string, string> {
  const resolvedTheme = options?.resolvedTheme ?? preferences.theme;
  const properties: Record<string, string> = {
    appearance_source: preferences.source,
    appearance_mode: preferences.mode,
    appearance_theme: preferences.theme,
    appearance_font: preferences.font,
    appearance_font_size: preferences.fontSize,
    appearance_resolved_mode: resolvedMode,
    appearance_resolved_theme: resolvedTheme,
  };

  if (preferences.source === "solar" && options?.solarPhase) {
    properties.appearance_solar_phase = options.solarPhase;
  }

  return properties;
}

export function syncAppearancePosthogProperties(
  preferences: AppearancePreferences,
  resolvedMode: ResolvedAppearanceMode,
  options?: {
    solarPhase: SolarCycleResolution["phase"] | null;
    resolvedTheme?: AppearancePreferences["theme"];
  }
): void {
  registerPosthogProperties(buildAppearancePosthogProperties(preferences, resolvedMode, options));
}

export function buildAppearancePreferencesChangedProperties(
  preference: AppearancePreferenceKey,
  value: string,
  previousValue: string,
  preferences: AppearancePreferences,
  prefersDark: boolean,
  solarResolution: SolarAnalyticsContext = null
): AppearancePreferencesChangedProperties {
  const resolvedMode =
    preferences.source === "solar" && solarResolution
      ? solarResolution.mode
      : resolveAppearanceMode(preferences.mode, prefersDark);
  const resolvedTheme =
    preferences.source === "solar" && solarResolution ? solarResolution.theme : preferences.theme;

  const properties: AppearancePreferencesChangedProperties = {
    preference,
    value,
    previous_value: previousValue,
    source: preferences.source,
    mode: preferences.mode,
    theme: preferences.theme,
    font: preferences.font,
    font_size: preferences.fontSize,
    resolved_mode: resolvedMode,
    resolved_theme: resolvedTheme,
  };

  if (preferences.source === "solar" && solarResolution) {
    properties.solar_phase = solarResolution.phase;
  }

  return properties;
}

export function appearanceValueForPreference(
  preferences: AppearancePreferences,
  preference: AppearancePreferenceKey
): string {
  switch (preference) {
    case "source":
      return preferences.source;
    case "mode":
      return preferences.mode;
    case "theme":
      return preferences.theme;
    case "font":
      return preferences.font;
    case "font_size":
      return preferences.fontSize;
    default: {
      const exhaustiveCheck: never = preference;
      return exhaustiveCheck;
    }
  }
}
